import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2,
    Building2,
    RefreshCw,
    Share2,
    Copy,
    MessageCircle,
    Mail,
    BarChart3,
} from "lucide-react";
import { AvailabilityBadge, STATUS_OPTIONS_SELL } from "@/components/landing/AvailabilityBadge";
import { ShareButtons } from "@/components/landing/ShareButtons";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

export const AvailabilityPanel = () => {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [aptRes, statsRes] = await Promise.all([
                api.get("/apartments"),
                api.get("/shares/stats").catch(() => ({ data: null })),
            ]);
            setItems(aptRes.data || []);
            setStats(statsRes.data || null);
        } catch (err) {
            toast.error("Konnte Verfügbarkeiten nicht laden", {
                description:
                    formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateStatus = async (apartmentId, newStatus) => {
        setSavingId(apartmentId);
        try {
            await api.patch(`/apartments/${apartmentId}/availability`, {
                status: newStatus,
            });
            await load();
            toast.success("Status aktualisiert");
        } catch (err) {
            toast.error("Status-Update fehlgeschlagen", {
                description:
                    formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-availability-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <span className="overline">Verkaufs-Status</span>
                    <h2 className="font-heading text-2xl text-ink mt-1">
                        Wohnungs-Verfügbarkeit
                    </h2>
                    <p className="font-body text-sm text-stone-500 mt-1">
                        Status pro Wohnung – wird sofort auf der öffentlichen Website angezeigt.
                    </p>
                </div>
                <button onClick={load} className="btn-ghost" data-testid="availability-refresh">
                    <RefreshCw size={14} />
                    Aktualisieren
                </button>
            </div>

            {/* Share stats summary */}
            {stats && (
                <div
                    className="bg-white border border-stone-200 p-5 mb-6 flex flex-wrap items-center gap-x-8 gap-y-3"
                    data-testid="share-stats-summary"
                >
                    <div className="flex items-center gap-3">
                        <BarChart3 size={18} className="text-brand-500" />
                        <div>
                            <div className="overline text-stone-400">Geteilte Wohnungen</div>
                            <div className="font-heading text-lg text-ink leading-tight">
                                <span data-testid="share-stats-total">{stats.total}</span>
                                <span className="text-stone-400 text-sm font-body ml-1">
                                    gesamt
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="overline text-stone-400">Letzte 30 Tage</div>
                        <div
                            className="font-heading text-lg text-ink"
                            data-testid="share-stats-30d"
                        >
                            {stats.total_30d}
                        </div>
                    </div>
                    <div>
                        <div className="overline text-stone-400">Zuletzt geteilt</div>
                        <div className="font-body text-sm text-stone-700">
                            {fmtDateTime(stats.last_at)}
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="p-12 flex justify-center">
                    <Loader2 className="animate-spin text-stone-400" size={24} />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((apt) => {
                        const aptStats = stats?.per_apartment?.[apt.id];
                        return (
                            <div
                                key={apt.id}
                                className="bg-white border border-stone-200 p-6 flex flex-col gap-4"
                                data-testid={`availability-card-${apt.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Building2
                                            size={18}
                                            className="text-brand-500 mt-1 shrink-0"
                                        />
                                        <div>
                                            <h3 className="font-heading text-lg text-ink leading-tight">
                                                {apt.name}
                                            </h3>
                                            <p className="font-body text-xs text-stone-500 mt-0.5">
                                                {apt.position} · {apt.size_m2} m² · {apt.price_label}
                                            </p>
                                        </div>
                                    </div>
                                    <AvailabilityBadge status={apt.status} />
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {STATUS_OPTIONS_SELL.map((s) => {
                                        const active = apt.status === s.value;
                                        const disabled = savingId === apt.id;
                                        return (
                                            <button
                                                key={s.value}
                                                onClick={() => !active && updateStatus(apt.id, s.value)}
                                                disabled={active || disabled}
                                                className={`px-3 py-1.5 text-xs font-body font-medium border transition-all ${
                                                    active
                                                        ? "bg-ink text-white border-ink"
                                                        : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
                                                } ${disabled ? "opacity-60" : ""}`}
                                                data-testid={`availability-set-${apt.id}-${s.value}`}
                                            >
                                                {s.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="pt-3 border-t border-stone-200">
                                    <ShareButtons
                                        apartmentId={apt.id}
                                        label="Diese Wohnung teilen"
                                    />
                                </div>

                                {/* Per-apartment share counter */}
                                {aptStats && (
                                    <div
                                        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-body text-stone-500 pt-2 border-t border-dashed border-stone-200"
                                        data-testid={`share-stats-card-${apt.id}`}
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <Share2 size={11} className="text-brand-500" />
                                            <span className="font-medium text-ink">
                                                {aptStats.total}×
                                            </span>
                                            <span>geteilt</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Copy size={11} />
                                            <span>{aptStats.copy}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <MessageCircle size={11} />
                                            <span>{aptStats.whatsapp}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Mail size={11} />
                                            <span>{aptStats.email}</span>
                                        </div>
                                    </div>
                                )}

                                {apt.status_updated_at && (
                                    <p className="font-body text-[11px] text-stone-400">
                                        Status zuletzt geändert: {fmtDateTime(apt.status_updated_at)}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
